// Оболочка приложения по образцу ERP: top-bar Orbis + собственное меню OMA слева + содержимое; шторка задачи; уведомления.
import { useEffect, useState } from 'react'
import { checkAuth, useAuth } from './auth.ts'
import { Icon } from './components/Icon.tsx'
import { Sidebar } from './components/Sidebar.tsx'
import { Toasts } from './components/Toasts.tsx'
import { Topbar, usePauseToggle } from './components/Topbar.tsx'
import { Button, Loading, cx } from './components/ui.tsx'
import { AgentPage } from './pages/AgentPage.tsx'
import { AgentsPage } from './pages/AgentsPage.tsx'
import { BoardPage } from './pages/BoardPage.tsx'
import { ChatsPage } from './pages/ChatsPage.tsx'
import { MachinesPage } from './pages/MachinesPage.tsx'
import { FeedPage } from './pages/FeedPage.tsx'
import { InboxPage } from './pages/InboxPage.tsx'
import { KnowledgePage } from './pages/KnowledgePage.tsx'
import { LoginPage } from './pages/LoginPage.tsx'
import { MapPage } from './pages/MapPage.tsx'
import { SettingsPage } from './pages/SettingsPage.tsx'
import { TaskDrawer } from './pages/TaskDrawer.tsx'
import { Link, isTaskPath, matchPath, useLocation } from './router.tsx'
import { loadDocUnits } from './lib/docs.ts'
import { startLive, stopLive, useLive } from './store.ts'

/** Страницы, которые занимают всю высоту и прокручиваются внутри себя. */
const FULL_HEIGHT = new Set(['/', '/map'])

function PageFor({ path }: { path: string }) {
  if (path === '/' || path === '') return <BoardPage />
  if (path === '/inbox') return <InboxPage />
  if (path === '/agents') return <AgentsPage />
  if (path === '/chats') return <ChatsPage />
  if (path === '/machines') return <MachinesPage />
  const agent = matchPath('/agents/:id', path)
  if (agent) return <AgentPage key={agent.id} id={agent.id!} />
  if (path === '/map') return <MapPage />
  if (path === '/feed') return <FeedPage />
  if (path === '/knowledge' || path === '/knowledge/docs') return <KnowledgePage />
  const doc = matchPath('/knowledge/docs/:unit', path)
  if (doc) return <KnowledgePage docUnit={doc.unit!} />
  if (path === '/settings') return <SettingsPage />
  return (
    <div className="page">
      <div className="crumb">404</div>
      <h1 className="h1">Такой страницы нет</h1>
      <p className="lede">
        <Link to="/" className="text-accent hover:underline">
          На доску
        </Link>
      </p>
    </div>
  )
}

function Shell() {
  const loc = useLocation()
  const task = isTaskPath(loc.pathname) ? Number(loc.pathname.split('/')[2]) : null
  const bgFull = task !== null ? (loc.state?.bg ?? '/') : loc.pathname + loc.search
  const bgPath = (bgFull.split('?')[0] || '/').replace(/\/+$/, '') || '/'

  useEffect(() => {
    startLive()
    void loadDocUnits().catch(() => undefined) // список справочников нужен карточкам агентов и знаниям
    return () => stopLive()
  }, [])

  useEffect(() => {
    const titles: Record<string, string> = {
      '/': 'Доска',
      '/inbox': 'Ждёт тебя',
      '/agents': 'Агенты',
      '/chats': 'Чаты',
      '/machines': 'Машины',
      '/map': 'Карта',
      '/feed': 'Эфир',
      '/knowledge': 'Знания',
      '/settings': 'Настройки',
    }
    const base = task !== null ? `OMA-${task}` : (titles[bgPath] ?? (bgPath.startsWith('/agents/') ? 'Агент' : bgPath.startsWith('/knowledge/docs') ? 'Справочники' : 'OMA'))
    document.title = `${base} · OMA`
  }, [task, bgPath])

  const [menuOpen, setMenuOpen] = useState(false)
  const pause = usePauseToggle()
  const stale = useLive((s) => s.stale)
  useEffect(() => {
    setMenuOpen(false)
  }, [bgPath, task])

  const full = FULL_HEIGHT.has(bgPath)
  return (
    <div className={cx('app', menuOpen && 'menu-open')}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[80] focus:rounded focus:bg-surface focus:px-3 focus:py-1.5 focus:shadow-pop"
      >
        К содержимому
      </a>
      {stale ? (
        <div className="pausebar" role="status">
          <Icon name="refresh" size={14} />
          <span>OMA обновилась — обновите страницу, чтобы видеть новые карточки и кнопки</span>
          <Button size="xs" variant="primary" onClick={() => window.location.reload()}>
            Обновить
          </Button>
        </div>
      ) : null}
      {pause.paused ? (
        <div className="pausebar" role="status">
          <Icon name="pause" size={14} />
          <span>Глобальная пауза — новые запуски агентов не стартуют</span>
          <Button size="xs" variant="secondary" onClick={() => void pause.toggle()} loading={pause.busy}>
            Снять паузу
          </Button>
        </div>
      ) : null}
      <Topbar menuOpen={menuOpen} onMenu={() => setMenuOpen((v) => !v)} />
      <Sidebar onNavigate={() => setMenuOpen(false)} />
      {menuOpen ? (
        <div className="backdrop fixed inset-x-0 top-[56px] bottom-0 z-40 min-[900px]:hidden" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      ) : null}
      <main id="main" data-scroll-root className={cx('main scroll-thin', full && 'full')}>
        <PageFor path={bgPath} />
      </main>
      {task !== null && Number.isFinite(task) ? <TaskDrawer id={task} /> : null}
    </div>
  )
}

export function App() {
  const auth = useAuth()
  useEffect(() => {
    void checkAuth()
  }, [])
  return (
    <>
      {auth.status === 'loading' ? (
        <div className="mx-auto flex h-full max-w-sm flex-col justify-center px-6">
          <Loading label="OMA загружается…" />
        </div>
      ) : auth.status === 'anon' ? (
        <LoginPage />
      ) : (
        <Shell />
      )}
      <Toasts />
    </>
  )
}
